// Monster Library component for the map editor drawer

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * @typedef {{
 *   slug: string,
 *   name: string,
 *   cr: string,
 *   creatureType: string,
 *   tokenSlug: string,
 *   tokenImageUrl: string,
 *   tokenSize: string,
 *   tokenBorderColor: string,
 * }} MonsterTemplate
 *
 * @typedef {{
 *   page: number,
 *   pageSize: number,
 *   query: string | undefined,
 * }} FetchMonstersParams
 *
 * @typedef {{
 *   monsters: MonsterTemplate[],
 *   totalCount: number,
 *   hasMore: boolean,
 * }} FetchMonstersResult
 */

/**
 * Monster Library component
 * Displays a paginated list of monsters that can be dragged onto the map
 */
class MonsterLibrary extends BaseComponent {
  static properties = {
    fetchMonsters: { type: Object },
    _monsters: { type: Array, state: true },
    _currentPage: { type: Number, state: true },
    _hasMore: { type: Boolean, state: true },
    _isLoading: { type: Boolean, state: true },
  };

  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      gap: 10px;
      height: 100%;
    }

    .monster-library-header {
      color: white;
      font-size: 18px;
      font-weight: 600;
      margin: 0 0 8px 0;
      padding-bottom: 10px;
      margin-bottom: 10px;
      border-bottom: 1px solid #444;
    }

    .loading-state,
    .empty-state {
      color: #999;
      font-size: 14px;
      padding: 20px;
      text-align: center;
    }

    .monster-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
      flex: 1;
      overflow-y: auto;
    }

    .monster-item {
      display: flex;
      align-items: center;
      gap: 12px;
      background: #333;
      border: 1px solid #555;
      border-radius: 8px;
      padding: 12px;
      backdrop-filter: blur(4px);
      transition: all 0.2s;
      cursor: pointer;
      user-select: none;
    }

    .monster-item:hover {
      background: #444;
      border-color: #666;
    }

    .monster-thumbnail {
      width: 60px;
      height: 60px;
      border: 2px solid #666;
      border-radius: 50%;
      overflow: hidden;
      background-color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .monster-thumbnail img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .no-token {
      color: #999;
      font-size: 24px;
      font-weight: bold;
    }

    .monster-info {
      display: flex;
      flex-direction: column;
      gap: 4px;
      flex: 1;
    }

    .monster-name {
      color: white;
      font-size: 14px;
    }

    .monster-stats {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      color: #999;
    }

    .stat {
      color: #ccc;
    }

    .stat-separator {
      color: #555;
    }

    .pagination {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding-top: 10px;
      border-top: 1px solid #444;
    }

    .pagination-button {
      background: #333;
      border: 1px solid #555;
      border-radius: 6px;
      color: #fff;
      padding: 8px 16px;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
    }

    .pagination-button:hover:not(:disabled) {
      background: #444;
      border-color: #666;
    }

    .pagination-button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .pagination-info {
      color: #ccc;
      font-size: 12px;
    }
  `;

  constructor() {
    super();
    /** @type {((params: FetchMonstersParams) => Promise<FetchMonstersResult>) | null} */
    this.fetchMonsters = null;
    /** @type {MonsterTemplate[]} */
    this._monsters = [];
    this._currentPage = 0;
    this._hasMore = false;
    this._isLoading = false;
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._loadMonsters();
  }

  async _loadMonsters() {
    if (!this.fetchMonsters) return;
    this._isLoading = true;
    try {
      const result = await this.fetchMonsters({
        page: this._currentPage,
        pageSize: 10,
        query: undefined,
      });
      this._monsters = result.monsters;
      this._hasMore = result.hasMore;
    } catch (error) {
      console.error('Failed to load monsters:', error);
      this._monsters = [];
      this._hasMore = false;
    } finally {
      this._isLoading = false;
    }
  }

  _handlePreviousPage() {
    if (this._currentPage > 0) {
      this._currentPage--;
      this._loadMonsters();
    }
  }

  _handleNextPage() {
    if (this._hasMore) {
      this._currentPage++;
      this._loadMonsters();
    }
  }

  /**
   * @param {MouseEvent} event
   * @param {MonsterTemplate} monster
   */
  _handleMouseDown(event, monster) {
    if (event.button !== 0) return;
    this.dispatchEvent(new CustomEvent('start-drag', { detail: monster, bubbles: true, composed: true }));
    event.preventDefault();
    event.stopPropagation();
  }

  render() {
    return html`
      <h2 class="monster-library-header">Monster Library</h2>

      ${
        this._isLoading
          ? html` <div class="loading-state">Loading...</div> `
          : this._monsters.length === 0
            ? html` <div class="empty-state">No monsters found</div> `
            : html`
              <div class="monster-list">
                ${this._monsters.map(
                  (monster) => html`
                    <div
                      class="monster-item"
                      @mousedown="${(/** @type {MouseEvent} */ e) => this._handleMouseDown(e, monster)}"
                    >
                      <div class="monster-thumbnail" style="border-color: ${monster.tokenBorderColor || '#666'}">
                        ${
                          monster.tokenImageUrl
                            ? html` <img src="${monster.tokenImageUrl}" alt="${monster.name}" draggable="false" /> `
                            : html` <div class="no-token">?</div> `
                        }
                      </div>
                      <div class="monster-info">
                        <div class="monster-name">${monster.name}</div>
                        <div class="monster-stats">
                          <span class="stat">CR ${monster.cr}</span>
                          <span class="stat-separator">&bull;</span>
                          <span class="stat">${monster.creatureType}</span>
                        </div>
                      </div>
                    </div>
                  `,
                )}
              </div>

              <div class="pagination">
                <button
                  class="pagination-button"
                  ?disabled="${this._currentPage === 0}"
                  @click="${this._handlePreviousPage}"
                >
                  Previous
                </button>
                <span class="pagination-info">Page ${this._currentPage + 1}</span>
                <button class="pagination-button" ?disabled="${!this._hasMore}" @click="${this._handleNextPage}">
                  Next
                </button>
              </div>
            `
      }
    `;
  }
}

customElements.define('monster-library', MonsterLibrary);
